import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, ScanLine, X, Check, RotateCcw, Search, SlidersHorizontal, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Heart, Loader2, Hammer, Plus } from 'lucide-react';
import { CollectionInsights } from '@/components/CollectionInsights';
import { DeckBuilderTab } from '@/components/tabs/DeckBuilderTab';
import { BinderView } from '@/components/BinderView';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { RarityBadge } from '@/components/RarityBadge';
import { CardSkeleton } from '@/components/CardSkeleton';
import { useAuth } from '@/hooks/useAuth';
import { useCollection, useAddToCollection, useUpdateCollectionItem, type CollectionItem, type CardPrint } from '@/hooks/useCollection';
import { useCards } from '@/hooks/useCards';
import { useWishlist, useAddToWishlist, useRemoveFromWishlist } from '@/hooks/useWishlist';
import { useLivePrice } from '@/hooks/useLivePrice';
import { supabase } from '@/integrations/supabase/client';
import { getCardPrice, formatPrice } from '@/lib/card-prices';
import { collectionLineValue, collectionUnitPrice } from '@/lib/collectionValue';
import { treatmentLabel } from '@/lib/treatment';
import { TreatmentPicker } from '@/components/TreatmentPicker';
import type { Card } from '@/hooks/useCards';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

type ScanState = 'idle' | 'capturing' | 'compressing' | 'identifying' | 'confirming';
type SortBy = 'added' | 'ink' | 'set' | 'rarity' | 'name';
type ViewMode = 'collection' | 'sets' | 'binder' | 'wishlist' | 'decks';

const RARITY_ORDER: Record<string, number> = { Common: 0, Uncommon: 1, Rare: 2, 'Super Rare': 3, Epic: 4, Legendary: 5, Enchanted: 6, Iconic: 7, Special: 8 };

const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'stable' }) => {
  if (trend === 'up') return <TrendingUp className="h-3 w-3 text-ink-emerald" />;
  if (trend === 'down') return <TrendingDown className="h-3 w-3 text-ink-ruby" />;
  return <Minus className="h-3 w-3 text-muted-foreground" />;
};

export function CardsTab() {
  const { user } = useAuth();
  const { data: collection, isLoading } = useCollection();
  const { data: allCards } = useCards({});
  const addToCollection = useAddToCollection();
  const updateItem = useUpdateCollectionItem();
  const { data: wishlist } = useWishlist();
  const addToWishlist = useAddToWishlist();
  const removeFromWishlist = useRemoveFromWishlist();
  const wishlistIds = new Set((wishlist || []).map((w: any) => w.card_id));

  const [scanState, setScanState] = useState<ScanState>('idle');
  const [matchedCard, setMatchedCard] = useState<Card | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [selectedPrintId, setSelectedPrintId] = useState<string | null>(null);
  const [selectedPrint, setSelectedPrint] = useState<CardPrint | null>(null);
  const [selectedItem, setSelectedItem] = useState<CollectionItem | null>(null);
  const [search, setSearch] = useState('');
  const [inkFilter, setInkFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<SortBy>('added');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('collection');
  useEffect(() => {
    const onNav = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.tab === 'collection' && ['collection', 'sets', 'binder', 'wishlist', 'decks'].includes(d.subTab)) {
        setViewMode(d.subTab as ViewMode);
      }
    };
    window.addEventListener('lorescout:navigate-subtab', onNav);
    return () => window.removeEventListener('lorescout:navigate-subtab', onNav);
  }, []);
  const [expandedSet, setExpandedSet] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  let items = collection || [];

  // Filter
  if (search) {
    const s = search.toLowerCase();
    items = items.filter(i => i.card?.name?.toLowerCase().includes(s) || i.card?.subtitle?.toLowerCase().includes(s));
  }
  if (inkFilter !== 'All') {
    items = items.filter(i => i.card?.ink_color === inkFilter);
  }

  // Sort
  items = [...items].sort((a, b) => {
    switch (sortBy) {
      case 'name': return (a.card?.name || '').localeCompare(b.card?.name || '');
      case 'ink': return (a.card?.ink_color || '').localeCompare(b.card?.ink_color || '');
      case 'set': return (a.card?.set_name || '').localeCompare(b.card?.set_name || '');
      case 'rarity': return (RARITY_ORDER[b.card?.rarity || ''] || 0) - (RARITY_ORDER[a.card?.rarity || ''] || 0);
      default: return new Date(b.added_at).getTime() - new Date(a.added_at).getTime();
    }
  });

  const totalCards = (collection || []).reduce((acc, c) => acc + (c.quantity || 1), 0);
  const uniqueCards = (collection || []).length;
  const enchantedCount = (collection || []).filter(c => c.card?.enchanted).length;

  // Set completion data
  const setMap = new Map<string, { total: number; owned: number; cards: Card[]; ownedIds: Set<string> }>();
  if (allCards) {
    allCards.forEach(card => {
      if (!setMap.has(card.set_name)) {
        setMap.set(card.set_name, { total: 0, owned: 0, cards: [], ownedIds: new Set() });
      }
      const entry = setMap.get(card.set_name)!;
      entry.total++;
      entry.cards.push(card);
    });
  }
  // Mark owned cards
  const ownedCardIds = new Set((collection || []).map(c => c.card_id));
  setMap.forEach((entry) => {
    entry.cards.forEach(card => {
      if (ownedCardIds.has(card.id)) {
        entry.owned++;
        entry.ownedIds.add(card.id);
      }
    });
  });
  const sets = Array.from(setMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));

  // Collection total value — uses real per-print prices when available
  const collectionValue = (collection || []).reduce((acc, item) => acc + collectionLineValue(item), 0);

  const [scanStep, setScanStep] = useState<string>('');
  const [scanProgress, setScanProgress] = useState(0);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setScanStep('Compressing image...');
    setScanProgress(15);
    setScanState('compressing');
    try {
      const { compressImageToBase64 } = await import('@/lib/image-utils');
      const { base64 } = await compressImageToBase64(file, { maxDim: 1024, quality: 0.82 });
      setCapturedImage(`data:image/jpeg;base64,${base64}`);
      setScanStep('Identifying card...');
      setScanProgress(60);
      setScanState('identifying');
      const { data, error } = await supabase.functions.invoke('identify-card', { body: { imageBase64: base64 } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const cardInfo = data.card;
      setScanStep('Finding match...');
      setScanProgress(85);
      let query = supabase.from('cards').select('*').ilike('name', `%${cardInfo.name}%`);
      if (cardInfo.subtitle) query = query.ilike('subtitle', `%${cardInfo.subtitle}%`);
      const { data: matches } = await query.limit(5);
      if (matches?.length) { setMatchedCard(matches[0] as Card); setScanState('confirming'); }
      else {
        const { data: broad } = await supabase.from('cards').select('*').ilike('name', `%${cardInfo.name}%`).limit(5);
        if (broad?.length) { setMatchedCard(broad[0] as Card); setScanState('confirming'); }
        else { toast.error('Could not find this card'); setScanState('idle'); }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to identify card');
      setScanState('idle');
    } finally {
      setScanStep('');
      setScanProgress(0);
    }
  }, []);

  const handleConfirm = async () => {
    if (!matchedCard) return;
    try {
      await addToCollection.mutateAsync({
        cardId: matchedCard.id,
        printId: selectedPrintId,
        photoUrl: capturedImage || undefined,
      });
      const label = selectedPrint ? treatmentLabel(selectedPrint.treatment) : 'Normal';
      toast.success(`Added (${label}) to your collection! ✨`);
      setScanState('idle'); setMatchedCard(null); setCapturedImage(null);
      setSelectedPrintId(null); setSelectedPrint(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleCancel = () => {
    setScanState('idle'); setMatchedCard(null); setCapturedImage(null);
    setSelectedPrintId(null); setSelectedPrint(null);
  };

  return (
    <div className="px-4 pb-4">
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />

      {/* Scan button */}
      <button
        onClick={() => { setScanState('capturing'); fileInputRef.current?.click(); }}
        disabled={!user || scanState === 'compressing' || scanState === 'identifying'}
        className="mt-3 flex w-full items-center justify-center gap-3 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 py-5 transition-colors hover:border-primary/60 hover:bg-primary/10 disabled:opacity-50"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
          {scanState === 'compressing' || scanState === 'identifying' ? <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /> : <Camera className="h-6 w-6 text-primary" />}
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="font-display text-lg font-semibold text-foreground">
            {scanState === 'compressing' || scanState === 'identifying' ? 'Scanning...' : 'Scan a Card'}
          </p>
          <p className="text-xs text-muted-foreground font-body">
            {user ? (scanState === 'compressing' || scanState === 'identifying' ? scanStep || 'Please wait...' : 'Take a photo or upload to identify & add') : 'Sign in to scan cards'}
          </p>
          {(scanState === 'compressing' || scanState === 'identifying') && (
            <div className="mt-2 h-1.5 w-full max-w-[200px] rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${scanProgress}%` }}
              />
            </div>
          )}
        </div>
      </button>

      {/* Stats bar */}
      <div className="mt-4 grid grid-cols-4 gap-2">
        {[
          { label: 'Total', value: totalCards },
          { label: 'Unique', value: uniqueCards },
          { label: 'Enchanted', value: enchantedCount },
          { label: 'Value', value: `$${collectionValue.toFixed(0)}` },
        ].map(s => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-2.5 text-center">
            <p className="font-display text-lg font-bold text-primary">{s.value}</p>
            <p className="text-[9px] text-muted-foreground font-body">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Insights dashboard — only on collection view */}
      {viewMode === 'collection' && <CollectionInsights />}

      {/* View mode toggle */}
      <div className="mt-3 flex gap-1 rounded-xl bg-secondary p-1 overflow-x-auto">
        <button onClick={() => setViewMode('collection')} className={`flex-1 min-w-[64px] rounded-lg py-2 text-xs font-medium font-body transition-colors ${viewMode === 'collection' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          My Cards
        </button>
        <button onClick={() => setViewMode('binder')} className={`flex-1 min-w-[64px] rounded-lg py-2 text-xs font-medium font-body transition-colors ${viewMode === 'binder' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          Binder
        </button>
        <button onClick={() => setViewMode('sets')} className={`flex-1 min-w-[56px] rounded-lg py-2 text-xs font-medium font-body transition-colors ${viewMode === 'sets' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          Sets
        </button>
        <button onClick={() => setViewMode('decks')} className={`flex-1 min-w-[56px] rounded-lg py-2 text-xs font-medium font-body transition-colors ${viewMode === 'decks' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          <span className="flex items-center justify-center gap-1"><Hammer className="h-3 w-3" />Decks</span>
        </button>
        <button onClick={() => setViewMode('wishlist')} className={`flex-1 min-w-[48px] rounded-lg py-2 text-xs font-medium font-body transition-colors ${viewMode === 'wishlist' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          ❤️
        </button>
      </div>

      {/* COLLECTION VIEW */}
      {viewMode === 'collection' && (
        <>
          {/* Search + filter toggle */}
          <div className="mt-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collection..." aria-label="Search your collection" className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${showFilters ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground'}`}>
              <SlidersHorizontal className="h-4 w-4" />
            </button>
          </div>

          {/* Filter/Sort panel */}
          {showFilters && (
            <div className="mt-2 rounded-xl border border-border bg-card p-3 space-y-3 animate-fade-in">
              <div>
                <p className="text-[10px] text-muted-foreground font-body mb-1.5">INK COLOR</p>
                <div className="flex flex-wrap gap-1.5">
                  {['All', 'Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'].map(ink => (
                    <button key={ink} onClick={() => setInkFilter(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${inkFilter === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                      {ink !== 'All' && <InkDot ink={ink as InkColor} size="sm" />}
                      {ink}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-body mb-1.5">SORT BY</p>
                <div className="flex flex-wrap gap-1.5">
                  {([['added', 'Recent'], ['name', 'Name'], ['ink', 'Ink'], ['set', 'Set'], ['rarity', 'Rarity']] as [SortBy, string][]).map(([val, label]) => (
                    <button key={val} onClick={() => setSortBy(val)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${sortBy === val ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Collection grid */}
          {isLoading ? (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-2">{Array.from({ length: 9 }).map((_, i) => <CardSkeleton key={i} />)}</div>
          ) : items.length === 0 ? (
            <div className="mt-8 py-12 text-center">
              <p className="text-4xl mb-3">🃏</p>
              <p className="text-sm text-muted-foreground font-body">{search || inkFilter !== 'All' ? 'No cards match your filters' : 'Your collection is empty'}</p>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-2">

              {items.map(item => {
                const card = item.card;
                if (!card) return null;
                const unit = collectionUnitPrice(item);
                const tLabel = item.print ? treatmentLabel(item.print.treatment) : null;
                return (
                  <div key={item.id} className="relative rounded-xl border border-border bg-card p-1.5 animate-fade-in cursor-pointer" onClick={() => setSelectedItem(item)}>
                    <CardImage src={item.photo_url || item.print?.image_url || card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} enchanted={card.enchanted || false} rarity={card.rarity} showGlow className="aspect-[2/3]" />
                    {(item.quantity || 1) > 1 && <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground font-body">{item.quantity}</span>}
                    {tLabel && tLabel !== 'Normal' && (
                      <span className="absolute top-3 left-3 rounded-full bg-background/90 px-1.5 py-0.5 text-[8px] font-medium text-primary font-body border border-primary/30">{tLabel}</span>
                    )}
                    <div className="mt-1.5 px-0.5">
                      <div className="flex items-center gap-1"><InkDot ink={card.ink_color as InkColor} size="sm" /><span className="truncate text-[10px] font-medium text-foreground font-body">{card.name}</span></div>
                      <div className="flex items-center justify-between">
                        {card.subtitle && <span className="text-[9px] text-muted-foreground font-body truncate">{card.subtitle}</span>}
                        <span className="text-[9px] font-medium text-primary font-body ml-auto">{formatPrice(unit)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* SET PROGRESS VIEW */}
      {viewMode === 'sets' && (
        <div className="mt-3 space-y-2">
          {sets.map(([setName, data]) => {
            const pct = data.total > 0 ? Math.round((data.owned / data.total) * 100) : 0;
            const isExpanded = expandedSet === setName;
            const missingCards = data.cards.filter(c => !data.ownedIds.has(c.id));
            return (
              <div key={setName} className="rounded-xl border border-border bg-card overflow-hidden animate-fade-in">
                <button onClick={() => setExpandedSet(isExpanded ? null : setName)} className="w-full p-4 text-left">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-foreground font-body">{setName}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-body">{data.owned}/{data.total}</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-ink-emerald' : pct > 50 ? 'bg-primary' : 'bg-primary/60'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[10px] font-medium font-body ${pct === 100 ? 'text-ink-emerald' : 'text-primary'}`}>{pct}% complete</span>
                    {pct === 100 && <span className="text-[10px] font-medium text-ink-emerald font-body">✨ Complete!</span>}
                  </div>
                </button>
                {isExpanded && missingCards.length > 0 && (
                  <div className="border-t border-border px-4 py-3 animate-fade-in">
                    <p className="text-[10px] text-muted-foreground font-body mb-2 uppercase tracking-wider">Missing Cards ({missingCards.length})</p>
                    <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
                      {missingCards.map(card => {
                        const wished = wishlistIds.has(card.id);
                        return (
                          <div key={card.id} className="flex items-center gap-1.5 rounded-lg bg-secondary px-2 py-1.5">
                            <InkDot ink={card.ink_color as InkColor} size="sm" />
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] text-foreground font-body truncate">{card.name}</p>
                              <p className="text-[9px] text-muted-foreground font-body">{card.rarity}</p>
                            </div>
                            <span className="text-[9px] text-primary font-body">{formatPrice(getCardPrice(card).mid)}</span>
                            <button
                              onClick={() => {
                                if (!user) { toast.error('Sign in to add to wishlist'); return; }
                                if (wished) {
                                  removeFromWishlist.mutate(card.id, { onSuccess: () => toast.success('Removed from wishlist') });
                                } else {
                                  addToWishlist.mutate({ cardId: card.id }, {
                                    onSuccess: () => toast.success(`${card.name} added to wishlist`),
                                    onError: (err: any) => toast.error(err.message || 'Could not add'),
                                  });
                                }
                              }}
                              className={`flex h-5 w-5 items-center justify-center rounded transition-colors ${wished ? 'bg-ink-ruby/20 text-ink-ruby' : 'bg-card text-muted-foreground hover:text-ink-ruby'}`}
                              aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                              {wished ? <Heart className="h-3 w-3 fill-current" /> : <Plus className="h-3 w-3" />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {isExpanded && missingCards.length === 0 && (
                  <div className="border-t border-border px-4 py-4 text-center animate-fade-in">
                    <p className="text-sm font-body text-ink-emerald">🎉 You have every card in this set!</p>
                  </div>
                )}
              </div>
            );
          })}
          {sets.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-4xl mb-3">📊</p>
              <p className="text-sm text-muted-foreground font-body">Card database loading...</p>
            </div>
          )}
        </div>
      )}

      {/* DECKS VIEW */}
      {/* BINDER VIEW */}
      {viewMode === 'binder' && <BinderView />}

      {/* DECKS VIEW */}
      {viewMode === 'decks' && <DeckBuilderTab />}

      {/* WISHLIST VIEW */}
      {viewMode === 'wishlist' && (
        <div className="mt-3 space-y-2">
          {!wishlist?.length ? (
            <div className="py-12 text-center">
              <p className="text-4xl mb-3">❤️</p>
              <p className="text-sm text-muted-foreground font-body">Your wishlist is empty</p>
              <p className="text-xs text-muted-foreground font-body mt-1">Add cards from the Library tab</p>
            </div>
          ) : (
            wishlist.map((item: any) => {
              const card = item.card;
              if (!card) return null;
              const price = getCardPrice(card);
              const owned = ownedCardIds.has(card.id);
              return (
                <div key={item.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 animate-fade-in">
                  <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} enchanted={card.enchanted || false} rarity={card.rarity} showGlow className="w-14 aspect-[2/3] flex-shrink-0 rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <InkDot ink={card.ink_color as InkColor} size="sm" />
                      <span className="text-sm font-medium text-foreground font-body truncate">{card.name}</span>
                    </div>
                    {card.subtitle && <p className="text-xs text-muted-foreground font-body truncate">{card.subtitle}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <RarityBadge rarity={card.rarity} />
                      <span className="text-xs text-primary font-body font-medium">{formatPrice(price.mid)}</span>
                      {owned && <span className="text-[10px] text-ink-emerald font-body">✓ Owned</span>}
                    </div>
                    {item.target_price && (
                      <p className="text-[10px] text-muted-foreground font-body mt-0.5">
                        Alert at: <span className="text-primary">{formatPrice(Number(item.target_price))}</span>
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeFromWishlist.mutate(card.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-ruby hover:bg-ink-ruby/10 transition-colors"
                  >
                    <Heart className="h-4 w-4 fill-current" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {scanState === 'confirming' && matchedCard && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={handleCancel}>
          <div className="animate-slide-up w-full max-w-[430px] rounded-t-2xl border border-border bg-card p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-foreground">Is this your card?</h3>
              <button onClick={handleCancel} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex gap-4">
              <CardImage src={selectedPrint?.image_url || matchedCard.image_url} alt={matchedCard.name} inkColor={matchedCard.ink_color as InkColor} enchanted={matchedCard.enchanted || false} rarity={matchedCard.rarity} showGlow className="w-32 aspect-[2/3] flex-shrink-0" />
              <div className="space-y-2 min-w-0">
                <h4 className="font-display text-base font-semibold text-foreground">{matchedCard.name}</h4>
                {matchedCard.subtitle && <p className="text-sm text-muted-foreground font-body">{matchedCard.subtitle}</p>}
                <div className="flex items-center gap-2"><InkDot ink={matchedCard.ink_color as InkColor} size="md" /><span className="text-sm font-body text-foreground">{matchedCard.ink_color}</span></div>
                <RarityBadge rarity={matchedCard.rarity} />
                <p className="text-xs text-muted-foreground font-body">{matchedCard.set_name}</p>
                {/* Price on confirm — prefers selected print price */}
                <div className="rounded bg-secondary px-2 py-1.5">
                  <span className="text-[10px] text-muted-foreground font-body">Market: </span>
                  <span className="text-xs font-medium text-primary font-body">
                    {formatPrice(selectedPrint?.price_usd != null ? Number(selectedPrint.price_usd) : getCardPrice(matchedCard).mid)}
                  </span>
                </div>
              </div>
            </div>

            {/* Treatment / printing selector */}
            <div className="mt-4">
              <TreatmentPicker
                cardId={matchedCard.id}
                value={selectedPrintId}
                onChange={(id, p) => { setSelectedPrintId(id); setSelectedPrint(p); }}
              />
              <p className="mt-1.5 text-[10px] text-muted-foreground font-body">
                Each treatment is tracked as its own copy, so values reflect what you actually own.
              </p>
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={handleConfirm} disabled={addToCollection.isPending} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
                <Check className="h-4 w-4" />{addToCollection.isPending ? 'Adding...' : 'Yes, Add'}
              </button>
              <button onClick={() => { setScanState('capturing'); fileInputRef.current?.click(); }} className="flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-4 py-3 text-sm font-medium text-secondary-foreground font-body">
                <RotateCcw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection item detail with price */}
      {selectedItem?.card && (() => {
        const card = selectedItem.card!;
        const fallback = getCardPrice(card);
        const unit = collectionUnitPrice(selectedItem);
        const isReal = selectedItem.print?.price_usd != null;
        const tLabel = selectedItem.print ? treatmentLabel(selectedItem.print.treatment) : 'Normal';
        return (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setSelectedItem(null)}>
            <div className="animate-slide-up w-full max-w-[430px] max-h-[85vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-foreground">{card.name}</h3>
                <button onClick={() => setSelectedItem(null)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
              </div>
              <div className="flex gap-3">
                <CardImage src={selectedItem.photo_url || selectedItem.print?.image_url || card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} enchanted={card.enchanted || false} rarity={card.rarity} showGlow className="w-36 aspect-[2/3] flex-shrink-0" />
                <div className="space-y-2 min-w-0">
                  {card.subtitle && <p className="text-sm text-muted-foreground font-body">{card.subtitle}</p>}
                  <div className="flex items-center gap-2"><InkDot ink={card.ink_color as InkColor} size="md" /><span className="text-sm font-body text-foreground">{card.ink_color}</span><RarityBadge rarity={card.rarity} /></div>
                  <p className="text-xs text-muted-foreground font-body">{card.set_name}</p>
                  <span className="inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary font-body">{tLabel}</span>
                  <div className="flex gap-1.5 text-xs font-body">
                    {card.cost != null && <span className="rounded bg-secondary px-1.5 py-0.5 text-foreground">⬡ {card.cost}</span>}
                    {card.strength != null && <span className="rounded bg-secondary px-1.5 py-0.5 text-foreground">⚔ {card.strength}</span>}
                    {card.willpower != null && <span className="rounded bg-secondary px-1.5 py-0.5 text-foreground">🛡 {card.willpower}</span>}
                    {card.lore_value != null && <span className="rounded bg-secondary px-1.5 py-0.5 text-foreground">✦ {card.lore_value}</span>}
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs text-muted-foreground font-body">Qty:</span>
                    <button onClick={() => updateItem.mutate({ id: selectedItem.id, quantity: (selectedItem.quantity || 1) - 1 })} className="flex h-7 w-7 items-center justify-center rounded bg-secondary text-foreground hover:bg-accent">−</button>
                    <span className="font-body text-sm font-medium text-foreground">{selectedItem.quantity || 1}</span>
                    <button onClick={() => updateItem.mutate({ id: selectedItem.id, quantity: (selectedItem.quantity || 1) + 1 })} className="flex h-7 w-7 items-center justify-center rounded bg-secondary text-foreground hover:bg-accent">+</button>
                  </div>
                </div>
              </div>

              {/* Price section */}
              <div className="mt-4 rounded-xl border border-border bg-secondary p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-foreground font-body flex items-center gap-1.5">
                    💰 {tLabel} Price {!isReal && <TrendIcon trend={fallback.trend} />}
                  </h4>
                  <span className="text-[10px] text-muted-foreground font-body">
                    Total: <span className="text-primary font-medium">{formatPrice(unit * (selectedItem.quantity || 1))}</span>
                  </span>
                </div>
                {isReal ? (
                  <div className="text-center">
                    <p className="text-sm font-bold text-primary font-body">{formatPrice(unit)}</p>
                    <p className="text-[10px] text-muted-foreground font-body">Latest market snapshot · per copy</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground font-body">Low</p>
                      <p className="text-sm font-medium text-foreground font-body">{formatPrice(fallback.low)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground font-body">Market</p>
                      <p className="text-sm font-bold text-primary font-body">{formatPrice(fallback.mid)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground font-body">High</p>
                      <p className="text-sm font-medium text-foreground font-body">{formatPrice(fallback.high)}</p>
                    </div>
                  </div>
                )}
              </div>

              {card.abilities && <div className="mt-4"><p className="text-xs text-muted-foreground font-body mb-1">Abilities</p><p className="text-sm text-foreground font-body leading-relaxed">{card.abilities}</p></div>}
              {card.flavor_text && <div className="mt-3"><p className="text-xs text-muted-foreground font-body italic leading-relaxed">{card.flavor_text}</p></div>}
              <button onClick={() => { updateItem.mutate({ id: selectedItem.id, quantity: 0 }); setSelectedItem(null); }} className="mt-4 w-full rounded-lg border border-destructive/30 py-2.5 text-xs font-medium text-destructive font-body hover:bg-destructive/10 transition-colors">
                Remove from Collection
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
